import { useQuery } from "@tanstack/react-query";

import { usePermissions } from "@/hooks/usePermissions";

import { briefKeys } from "../queryKeys";
import { fetchBrief } from "../repository";
import type { BriefFinding, ProjectBrief } from "../types";
import { useBriefQueriesEnabled } from "./useBrief";
import { useBriefFindings } from "./useBriefData";
import { useBriefSchemaAvailable } from "./useBriefSchema";

export type BriefCardState =
  | { kind: "hidden" }
  | { kind: "loading" }
  | { kind: "notStarted"; canStart: boolean }
  | { kind: "suggestions"; suggestions: BriefFinding[] }
  /** A brief with no open suggestions. */
  | { kind: "noSuggestions" }
  /** The brief could not be read — it may well exist, so never "Start". */
  | { kind: "unavailable" };

function startedBriefId(brief: ProjectBrief | null | undefined): number | null {
  return brief != null && brief.folder_id != null ? brief.id : null;
}

function loadedCardState(
  started: boolean,
  canStart: boolean,
  suggestions: BriefFinding[]
): BriefCardState {
  if (!started) return { kind: "notStarted", canStart };
  return suggestions.length > 0 ? { kind: "suggestions", suggestions } : { kind: "noSuggestions" };
}

function cardReadState(
  allowed: boolean,
  available: boolean | null | undefined,
  ready: boolean,
  brief: { isLoading: boolean; isError: boolean }
): BriefCardState | null {
  if (!allowed || available === false) return { kind: "hidden" };
  if (!ready || brief.isLoading) return { kind: "loading" };
  return brief.isError ? { kind: "unavailable" } : null;
}

/**
 * What the dashboard's Brief card shows for one project.
 *
 * The gate is the sidebar tab's (VIEW_BRIEFS on the project, and the brief
 * schema deployed), so the card and the tab appear and disappear together.
 * "Not started" is the Brief page's own rule — no brief row, or one with no
 * folder (`Brief.tsx` `hasFolder`). A failed read never offers "Start the
 * brief": the brief may well exist (`BriefUnavailable`), so it is its own
 * state, and the card says "Go to brief" and lets the page explain.
 *
 * Reads the Brief page's cache entries (`briefKeys.root` / `.findings`, same
 * fetchers), so visiting one after the other costs nothing, and the
 * building-wide brief channel BuildingLayout already runs keeps the findings
 * fresh.
 */
export function useBriefCardState(projectId: number): BriefCardState {
  const perms = usePermissions();
  const allowed = perms.canViewBriefsOnProject(projectId);
  const enabled = useBriefQueriesEnabled(allowed ? projectId : null);
  const { available } = useBriefSchemaAvailable(enabled);
  const ready = enabled && available === true;

  const brief = useQuery({
    queryKey: briefKeys.root(projectId),
    queryFn: () => fetchBrief(projectId),
    enabled: ready,
  });
  const briefId = startedBriefId(brief.data);
  const started = briefId !== null;
  const findings = useBriefFindings(briefId, ready && started);

  const pending = cardReadState(allowed, available, ready, brief);
  if (pending) return pending;
  return loadedCardState(started, perms.canManageBriefsOnProject(projectId), findings.suggestions);
}
